// scheduling/components/reports/SignInsReport.jsx
// Sign-in / adoption report (Trello #309).
//
// DJB: "A last sign-in date would be good. If it's also easy to show # of sign ins
// (with a filter for time period) that would help but not crucial."
//
// Lists every ACTIVE, ALREADY-STARTED staff member — including those who have never
// signed in. That inversion is the point: "who has never opened RPRS" is the adoption
// question, and it is invisible in a report that only lists rows which exist.
//
// The counts are sessions, not requests. See api/rprsReports/signins.js for why
// rprs.AuthLog cannot answer this directly (the 60s notifications poll, a per-Function-
// instance de-dupe, and 11,581 localhost rows whose signal is in RequestPath rather
// than ClientIP).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchSignIns } from '../../services/reportsApi';
import { useSort, SortableTh, PlainTh } from './SortableTh';

const PERIOD_OPTIONS = [
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
  { id: 365, label: '1 year' },
  { id: 0, label: 'All time' },
];

// Recency buckets. Chosen against how the app is actually used: RPRS is a weekly
// scheduling tool, so "this week" is healthy, a month is drifting, and never is the
// number worth acting on.
function recencyOf(lastEver) {
  if (!lastEver) return { key: 'never', label: 'Never', bg: '#F3F4F6', fg: '#4B5563', days: null };
  const days = Math.floor((Date.now() - new Date(lastEver).getTime()) / 86400000);
  if (days <= 7) return { key: 'week', label: 'This week', bg: '#F0FDF4', fg: '#15803D', days };
  if (days <= 30) return { key: 'month', label: 'This month', bg: '#FFFBEB', fg: '#B45309', days };
  return { key: 'stale', label: `${days}d ago`, bg: '#FEF2F2', fg: '#B91C1C', days };
}

// ── Timezone ────────────────────────────────────────────────────────────────
// Sign-in times are stored and returned in UTC: rprs.SignIns.StartedAt defaults to
// GETUTCDATE(), and the backfill copies rprs.AuthLog.CreatedAt which does the same.
// Slicing the ISO string (the first version of this file) therefore DISPLAYED UTC — a
// 17:08 sign-in read as 17:08 when the person actually signed in at 12:08 Central.
//
// Formatted in America/Chicago rather than the browser's zone deliberately: BMSS is a
// Central-time firm, so "when did they sign in" should mean the same thing to a reader
// in Birmingham and one travelling, and it should match what people see in PE.
// Chicago (not a fixed -5/-6) so CST/CDT is handled by the runtime.
const TZ = 'America/Chicago';

// en-CA gives YYYY-MM-DD; en-GB with 2-digit parts gives 24-hour HH:MM.
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: TZ }) : '—');
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-CA', { timeZone: TZ })} `
    + `${d.toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}`;
};

const SORTS = {
  name: { get: r => String(r.name).toLowerCase(), dir: 'asc' },
  level: { get: r => String(r.level || ''), dir: 'asc' },
  dept: { get: r => String(r.dept || ''), dir: 'asc' },
  office: { get: r => String(r.office || ''), dir: 'asc' },
  // Newest-first when first picked — "who was here recently" is the common read.
  lastEver: { get: r => r.lastEver || '', dir: 'desc' },
  visitsInPeriod: { get: r => r.visitsInPeriod, dir: 'desc' },
  totalVisits: { get: r => r.totalVisits, dir: 'desc' },
  firstEver: { get: r => r.firstEver || '', dir: 'asc' },
};

// Add-from-dropdown plus removable pills. Deliberately not a checkbox popover: the
// option counts here are small (10 levels, ~6 offices, ~13 depts) and this shape is
// already used by the ad-hoc builder, so the reporting section stays internally
// consistent rather than growing a second multi-select idiom.
function MultiPick({ label, title, options, selected, onToggle }) {
  return (
    <span className="sched-rpt-bfield">
      <label className="sched-rpt-blabel" title={title}>{label}</label>
      <select className="sched-rpt-bsel" value=""
        onChange={e => { if (e.target.value) onToggle(e.target.value); }}>
        <option value="">{selected.length ? `+ add (${selected.length})` : 'all'}</option>
        {options.filter(o => !selected.includes(o)).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {selected.map(v => (
        <button key={v} className="sched-mgr-pill sched-mgr-pill--active"
          title="Click to remove" onClick={() => onToggle(v)}>{v} ×</button>
      ))}
    </span>
  );
}

export default function SignInsReport() {
  const navigate = useNavigate();
  const [days, setDays] = useUrlState('days', 90, { validate: PERIOD_OPTIONS.map(p => p.id) });
  const [onlyNever, setOnlyNever] = useUrlState('never', false);
  const sort = useSort(SORTS, 'lastEver', 'sisort');

  // Filters are client-side: the report returns one row per active staff member (~293),
  // so filtering locally is instant and avoids a re-query per checkbox.
  const [levels, setLevels] = useState([]);
  const [offices, setOffices] = useState([]);
  const [depts, setDepts] = useState([]);
  const [search, setSearch] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSignIns({ days })
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [days]);

  // Options come from the returned rows, not a hardcoded list, so they can never offer
  // a value that matches nothing or omit one that exists.
  const { levelOpts, officeOpts, deptOpts } = useMemo(() => {
    const uniq = (k) => [...new Set((data?.rows || []).map(r => r[k]).filter(Boolean))].sort();
    return { levelOpts: uniq('level'), officeOpts: uniq('office'), deptOpts: uniq('dept') };
  }, [data]);

  const { rows, tally, totalCount } = useMemo(() => {
    const all = (data?.rows || []).map(r => ({ ...r, recency: recencyOf(r.lastEver) }));
    // Tally is over ALL staff, not the filtered set — the headline "100 never signed in"
    // must not silently shrink when someone narrows to one office.
    const t = { week: 0, month: 0, stale: 0, never: 0 };
    all.forEach(r => { t[r.recency.key] = (t[r.recency.key] || 0) + 1; });

    const q = search.trim().toLowerCase();
    const filtered = all.filter(r =>
      (!onlyNever || r.recency.key === 'never')
      && (!levels.length || levels.includes(r.level))
      && (!offices.length || offices.includes(r.office))
      && (!depts.length || depts.includes(r.dept))
      && (!q || String(r.name).toLowerCase().includes(q) || String(r.email || '').toLowerCase().includes(q))
    );
    return { rows: sort.sortRows(filtered), tally: t, totalCount: all.length };
  }, [data, sort, onlyNever, levels, offices, depts, search]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const filtersActive = onlyNever || levels.length || offices.length || depts.length || search.trim();

  const periodLabel = PERIOD_OPTIONS.find(p => p.id === days)?.label || `${days} days`;
  const prov = data?.provenance;

  return (
    <div className="sched-rpt-root">
      <div className="sched-cap-toolbar">
        <span className="sched-cap-toolbar-label" title="Window for the “Visits” count. Last sign-in and lifetime totals are always all-time.">PERIOD</span>
        {PERIOD_OPTIONS.map(p => (
          <button key={p.id}
            className={`sched-mgr-pill${days === p.id ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setDays(p.id)}
          >{p.label}</button>
        ))}
      </div>

      {/* Filters. Client-side over ~293 rows, so they apply instantly. */}
      <div className="sched-rpt-builder">
        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <MultiPick label="Dept" title="Filter to one or more PE department codes. Useful for spotting a whole team that has not adopted the app."
            options={deptOpts} selected={depts} onToggle={v => toggle(depts, setDepts, v)} />
          <MultiPick label="Level" title="Filter to one or more PE staff levels."
            options={levelOpts} selected={levels} onToggle={v => toggle(levels, setLevels, v)} />
          <MultiPick label="Office" title="Filter to one or more PE office codes."
            options={officeOpts} selected={offices} onToggle={v => toggle(offices, setOffices, v)} />
        </div>
        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Substring match on staff name or email address.">Staff</label>
            <input className="sched-rpt-btext" placeholder="name or email contains…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </span>
          <label className="sched-rpt-check" title="Show only staff with no recorded sign-in at all — the adoption gap.">
            <input type="checkbox" checked={onlyNever} onChange={e => setOnlyNever(e.target.checked)} />
            Never signed in
          </label>
          {filtersActive ? (
            <span className="sched-rpt-muted-sm">
              Showing <strong>{rows.length}</strong> of {totalCount} staff
              <button className="sched-rpt-clear" onClick={() => {
                setLevels([]); setOffices([]); setDepts([]); setSearch(''); setOnlyNever(false);
              }}>clear filters</button>
            </span>
          ) : (
            <span className="sched-rpt-muted-sm">{totalCount} active staff</span>
          )}
        </div>
      </div>

      {error && <div className="sched-rpt-error">Couldn’t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading…</div>}

      {!loading && !error && data && (
        <>
          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l" title="Bucketed by most recent sign-in over all recorded history — not by the selected period, and not affected by the filters above.">
                Last seen &middot; all staff
              </div>
              <div className="sched-rpt-statrow">
                {[
                  { k: 'week', l: 'This week', bg: '#F0FDF4', fg: '#15803D' },
                  { k: 'month', l: 'This month', bg: '#FFFBEB', fg: '#B45309' },
                  { k: 'stale', l: 'Longer ago', bg: '#FEF2F2', fg: '#B91C1C' },
                  { k: 'never', l: 'Never', bg: '#F3F4F6', fg: '#4B5563' },
                ].map(s => (
                  <div key={s.k} className="sched-rpt-stat" style={{ background: s.bg }}
                    title={s.k === 'never'
                      ? 'Active staff with no recorded sign-in. History only goes back to the horizon noted opposite.'
                      : 'Staff whose most recent sign-in falls in this bucket'}>
                    <div className="sched-rpt-stat-n" style={{ color: s.fg }}>{tally[s.k] || 0}</div>
                    <div className="sched-rpt-stat-l">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sched-rpt-legend">
              {prov && (
                <>
                  {/* Stated plainly: most of this history is RECONSTRUCTED from auth
                      checks, not observed sign-ins, and a reader should know which. */}
                  <div className="sched-rpt-muted-sm">
                    History from <strong>{fmtDate(prov.horizon)}</strong>.{' '}
                    {prov.beaconRows.toLocaleString()} observed,{' '}
                    {prov.backfillRows.toLocaleString()} reconstructed from auth logs.
                  </div>
                  <div className="sched-rpt-muted-sm">
                    A “sign-in” is a gap-separated session ({prov.sessionGapMinutes} min), not a request —
                    the app polls for notifications every 60s, so raw auth rows are not visits.
                  </div>
                  {data.orphans?.rows > 0 && (
                    <div className="sched-rpt-muted-sm">
                      {data.orphans.rows.toLocaleString()} sign-ins from {data.orphans.emails} address(es)
                      not matching an active staff record (departed staff, admin accounts) are excluded below.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Its own scroll container, so the header stays put while the ~293 rows move.
              position:sticky needs a scrolling ancestor with a CONSTRAINED height — the
              page-level scroller has none, so without this the header scrolled away with
              the body. See .sched-rpt-scroll. */}
          <div className="sched-mgr-table-wrap sched-rpt-scroll">
            <table className="sched-mgr-table sched-rpt-table">
              <thead>
                <tr className="sched-rpt-colrow">
                  <SortableTh sort={sort} sortKey="name" label="Staff"
                    title="Active staff whose start date has passed. Click a row to open their profile." />
                  <SortableTh sort={sort} sortKey="level" label="Level" title="PE staff level." />
                  <SortableTh sort={sort} sortKey="dept" label="Dept" title="PE department code." />
                  <SortableTh sort={sort} sortKey="office" label="Office" title="PE office code." />
                  <SortableTh sort={sort} sortKey="lastEver" label="Last sign-in"
                    title="Most recent recorded session, all-time regardless of the selected period. Shown in Central time (CST/CDT) — stored as UTC." />
                  <PlainTh label="Ago"
                    title="How long since that sign-in. “Never” means no recorded session since the history horizon — not necessarily never in the app’s lifetime." />
                  <SortableTh sort={sort} sortKey="visitsInPeriod" label={`Visits (${periodLabel})`} className="sched-mgr-th-right"
                    title={`Gap-separated sessions within the selected period (${periodLabel}). Two tabs open, or an all-day idle tab, count once per 30-minute window — not once per request.`} />
                  <SortableTh sort={sort} sortKey="totalVisits" label="Total" className="sched-mgr-th-right"
                    title="Lifetime recorded sessions across all history, observed and reconstructed combined." />
                  <SortableTh sort={sort} sortKey="firstEver" label="First seen"
                    title="Earliest recorded session, Central time. Bounded by the history horizon, so for long-tenured staff this is when logging began, not when they started using RPRS." />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.staffId} onClick={() => navigate(`/schedule/employee/${r.staffId}`)}>
                    <td>{r.name}</td>
                    <td className="sched-rpt-muted-sm">{r.level}</td>
                    <td className="sched-rpt-muted-sm">{r.dept}</td>
                    <td className="sched-rpt-muted-sm">{r.office}</td>
                    <td>{fmtDateTime(r.lastEver)}</td>
                    <td>
                      <span className="sched-rpt-chip"
                        style={{ background: r.recency.bg, color: r.recency.fg }}
                        title={r.lastEver ? `Last session ${fmtDateTime(r.lastEver)}` : 'No recorded session'}>
                        {r.recency.label}
                      </span>
                    </td>
                    <td className="sched-mgr-td-right sched-rpt-pct">{r.visitsInPeriod}</td>
                    <td className="sched-mgr-td-right sched-rpt-muted-sm">{r.totalVisits}</td>
                    <td className="sched-rpt-muted-sm">{fmtDate(r.firstEver)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="sched-rpt-muted">
                    {onlyNever && !levels.length && !offices.length && !depts.length && !search.trim()
                      ? 'Every active staff member has signed in at least once.'
                      : 'No staff match these filters.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sched-rpt-footnote">
            <strong>Last sign-in</strong> and <strong>Total</strong> are all-time;{' '}
            <strong>Visits</strong> respects the period selector. A sign-in is a session — consecutive
            activity separated by more than {prov?.sessionGapMinutes || 30} minutes — because the app
            polls in the background and a request count would measure tab-uptime rather than use.
            History starts {fmtDate(prov?.horizon)}, so “Never” means “not since then”. The
            <em> Last seen</em> counts above cover all active staff and do not change with the filters.
          </div>
        </>
      )}
    </div>
  );
}
